/**
 * Convert a string to a URL-friendly slug.
 * Lowercases, strips non-alphanumerics, replaces whitespace with hyphens, and collapses repeats.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Turn a slug back into a readable title case string.
 * Useful as a fallback before canonical display names load.
 */
export function titleCaseFromSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Build the canonical region landing-page path for a region name.
 */
export function getRegionPathFromName(regionName: string): string {
  return `/regions/${slugify(regionName)}`;
}

/**
 * Normalize a venue name to the canonical base used in public venue URLs.
 */
export function getVenueBaseName(venueName: string): string {
  return venueName.split(",")[0]?.trim() || venueName.trim();
}

/**
 * Build the canonical venue landing-page path for a venue name.
 */
export function getVenuePathFromName(venueName: string): string {
  return `/venues/${slugify(getVenueBaseName(venueName))}`;
}

/**
 * The place without its street address or room, for page addresses and matching:
 * "Scotia Branch Library - Scotia Meeting Room, 14 Mohawk Ave" → "Scotia Branch Library".
 */
export function getShortVenueName(venueName: string): string {
  return getVenueBaseName(venueName).split(" - ")[0]?.trim() || getVenueBaseName(venueName);
}

const SERIES_READABLE_MAX_LENGTH = 80;

/**
 * Permanent page for a repeating event (same title, venue and time):
 * /series/<title>[-<venue>]-<first 8 characters of its series key>.
 * The key keeps the address unique; the words are for people and search engines.
 */
export function getSeriesPath(title: string, venueName: string, seriesKey: string): string {
  const titleSlug = slugify(title);
  const venueSlug = slugify(getShortVenueName(venueName));
  let readable = !venueSlug || titleSlug.includes(venueSlug) ? titleSlug : `${titleSlug}-${venueSlug}`;
  if (readable.length > SERIES_READABLE_MAX_LENGTH) {
    readable = readable.slice(0, SERIES_READABLE_MAX_LENGTH).replace(/-[^-]*$/, "");
  }
  return `/series/${readable ? `${readable}-` : ""}${seriesKey.slice(0, 8)}`;
}

/** The series key prefix at the end of a series page slug, or null if it has none. */
export function parseSeriesSlug(slug: string): { keyPrefix: string } | null {
  const match = slug.match(/(?:^|-)([0-9a-f]{8})$/);
  return match ? { keyPrefix: match[1] } : null;
}
